import { respondToSupplyLog } from '@backend/functions/gyn'
import { Alert, AlertIcon, Button, HStack, Text, VStack } from '@chakra-ui/react'
import { useApp, usePopUpMessage } from '@hb/react'
import { Encounter, SupplyLogEntry, WithId } from '@hb/shared'
import { useCallback, useState } from 'react'

export const CdiEntryPrompt = ({
  entry,
  encounterId,
  showHcpcsCode = false,
}: {
  entry: SupplyLogEntry
  encounterId: string
  // §0 / §1.5: billing codes must never render in provider UI. Default off; only admin
  // (billing) surfaces pass true. CdiEntryPrompt is provider-facing everywhere today.
  showHcpcsCode?: boolean
}) => {
  const [responding, setResponding] = useState(false)
  const { appName } = useApp()
  const { showError } = usePopUpMessage()

  const respond = useCallback(
    async (confirmed: boolean) => {
      setResponding(true)
      try {
        // Firestore rules allow no client writes to ENCOUNTERS — persist via the
        // provider-permitted callable, which touches only this entry's response fields.
        await respondToSupplyLog({
          encounterId,
          appName,
          action: 'respond',
          entryId: entry.id,
          confirmed,
        })
      } catch (err) {
        showError((err as Error)?.message ?? 'Could not save your response. Please try again.')
      } finally {
        setResponding(false)
      }
    },
    [encounterId, entry.id, appName, showError],
  )

  return (
    <Alert status="info" borderRadius={4} py={2} fontSize="sm" alignItems="flex-start">
      <AlertIcon mt={0.5} flexShrink={0} />
      <VStack align="flex-start" spacing={1} flex={1} minW={0}>
        <Text fontSize="sm">
          We noticed you documented{' '}
          <Text as="span" fontWeight={600}>
            {entry.keyword}
          </Text>
          . Add{' '}
          <Text as="span" fontWeight={600}>
            {entry.matchedSupply}
          </Text>{' '}
          to the supply log?
          {showHcpcsCode && entry.hcpcsCode ? (
            <Text as="span" color="gray.500" fontSize="xs">
              {' '}
              ({entry.hcpcsCode})
            </Text>
          ) : null}
        </Text>
        <HStack spacing={2}>
          <Button
            size="xs"
            colorScheme="green"
            isLoading={responding}
            onClick={() => respond(true)}>
            Yes — add to supply log
          </Button>
          <Button
            size="xs"
            variant="ghost"
            colorScheme="gray"
            isLoading={responding}
            onClick={() => respond(false)}>
            No
          </Button>
        </HStack>
      </VStack>
    </Alert>
  )
}

// §6.7 "Match action": one prompt per unconfirmed/undismissed CDI detection. Provider-facing
// only (mounted in EncounterForm) — no billing codes shown. The admin/billing app gets a
// read-only summary instead (CdiSummary), never this interactive banner.
export const CdiPromptBanner = ({
  encounter,
  encounterId,
}: {
  encounter: WithId<Encounter>
  encounterId: string
}) => {
  const pending = (encounter.supplyLog ?? []).filter(
    e => !e.confirmedByProvider && !e.dismissedByProvider,
  )
  if (pending.length === 0) return null

  return (
    <VStack w="100%" align="stretch" spacing={1} px={2} py={1}>
      {pending.map(entry => (
        <CdiEntryPrompt key={entry.id} entry={entry} encounterId={encounterId} />
      ))}
    </VStack>
  )
}
